import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, map, Observable, tap, throwError } from 'rxjs';
import { StateService } from '../state/state.service';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth/auth.service';
import { Router } from '@angular/router';
import { ModalController } from '@ionic/angular/standalone';
import {
  ChangeResponse,
  CheckInForm,
  DocumentResponse,
  FacilityCycle,
  FormType,
  PatientCycle,
  SessionData,
  VerificationCheck,
  VerificationRequest,
} from 'src/app/models/general.model';
import {
  Delivery,
  DocumentInterface,
  OrderPrescriptionDoctor,
  Prescription,
} from 'src/app/models/prescription.model';
import { SentSubmission } from 'src/app/models/submission.model';
import { Order } from 'src/app/models/order.model';
import { Blocker } from 'src/app/models/blocker.model';
import {
  ActionItem,
  ActionItemDetail,
  ActionItemUpdateRequest,
  ActionItemUpdateResponse,
  normalizeActionItem,
} from 'src/app/models/action-item.model';
import {
  Service,
  ServiceCategory,
  ServiceDraft,
  ServiceDraftSaveRequest,
  ServiceRequestDetail,
  ServiceRequestSubmitRequest,
  ServiceRequestSummary,
  asServiceId,
  mapCatalogService,
  mapServiceCatalog,
  mapServiceDraftDetail,
  mapServiceDraftListItem,
  mapServiceRequestDetail,
  mapServiceRequestSubmitResponse,
  mapServiceRequestSummary,
  toServiceDraftApiBody,
} from 'src/app/models/service.model';
import { ApplicationSettings } from 'src/app/models/settings.model';
import { Notifications } from 'src/app/models/notification.model';
import {
  PatientDetails,
  PatientDetailsdata,
} from 'src/app/models/patient.model';
import { Invoice } from 'src/app/models/invoice.model';
import { LoginUser, RegistrationCredentials } from 'src/app/models/user.model';

function serviceUpdatedAtTime(value: string): number {
  const parsed = Date.parse(value.trim().replace(' ', 'T'));
  return Number.isNaN(parsed) ? 0 : parsed;
}

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private baseUrl = environment.apiUrl;
  private headers = new HttpHeaders({
    'app-id': 'nlSwUjIBL3QpB8++8OWCog==',
  });

  private servicesBaseUrl = this.baseUrl;

  private servicesHttpOptions() {
    return { headers: this.headers, withCredentials: true };
  }

  constructor(
    private http: HttpClient,
    private stateService: StateService,
    private authService: AuthService,
    private router: Router,
    private modalCtrl: ModalController
  ) {}

  async dismissAllModals() {
    let topModal = await this.modalCtrl.getTop();
    while (topModal) {
      await this.modalCtrl.dismiss();
      topModal = await this.modalCtrl.getTop();
    }
  }

  async handleError403() {
    await this.dismissAllModals();

    this.authService.logout().subscribe({
      next: () => {
        // this.modalCtrl.dismiss(null);
        this.router.navigate(['/login'], {
          replaceUrl: true,
          queryParams: { logoutCause: 'session end' },
        });
      },
      error: (err: any) => {
        console.error('Logout failed:', err);
      },
    });
  }
  forogtPassword(username: string) {
    return this.http
      .post(
        `${this.baseUrl}/user/forgot-password`,
        { username: username },
        {
          headers: this.headers,
          withCredentials: true,
          responseType: 'text' as 'json',
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  forogtUsername(phone: string) {
    return this.http
      .post(
        `${this.baseUrl}/user/forgot-username`,
        { phone: phone },
        {
          headers: this.headers,
          withCredentials: true,
          responseType: 'text' as 'json',
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  initializeBrandingLabels(): Observable<ApplicationSettings> {
    return this.http
      .get<ApplicationSettings>(`${this.baseUrl}/tenant/settings`, {
        withCredentials: true,
      })
      .pipe(
        tap((response) => {
          this.headers = new HttpHeaders({
            'app-id': response.app_id,
          });
          console.log('SUCCEEDED ID', response.app_id);
        }),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403) {
            // this.handleError403();
          }
          // Asigna un valor por defecto en caso de error
          this.headers = new HttpHeaders({
            'app-id': 'nlSwUjIBL3QpB8++8OWCog==',
          });
          console.log('FAILED ID', error?.app_id ?? 'N/A');
          return throwError(() => error);
        })
      );
  }

  loginwithFA(
    username: string,
    password: string,
    deviceId: string,
    code: string
  ): Observable<LoginUser> {
    return this.http
      .post<LoginUser>(
        `${this.baseUrl}/user/login-2fa`,
        {
          username: username,
          password: password,
          device_id: deviceId,
          code: code,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getNotifications(): Observable<Notifications[]> {
    return this.http
      .get<Notifications[]>(`${this.baseUrl}/user/notifications`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        tap((notifications) =>
          this.stateService.setNotificationsCount(
            notifications.filter((n) => !n.opened).length
          )
        )
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => new Error('Failed to fetch notifications'));
        })
      );
  }

  openedNotification(notificationId: number): Observable<any> {
    return this.http
      .put(
        `${this.baseUrl}/user/notifications/${notificationId}/opened`,
        {},
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Returns the current user's action items.
   * Status/search filtering is done on the client; this endpoint has no query filters.
   */
  getActionItems(): Observable<ActionItem[]> {
    return this.http
      .get<ActionItem[]>(`${this.baseUrl}/action-items`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        map((items) => (items ?? []).map((item) => normalizeActionItem(item))),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Returns a single action item. Checklist details include checklistItems;
   * form details include form.schema and form.inputData.
   * Callers should handle 409 ACTION_ITEM_ALREADY_COMPLETED and 404 NOT_FOUND.
   */
  getActionItemById(id: number): Observable<ActionItemDetail> {
    return this.http
      .get<ActionItemDetail>(`${this.baseUrl}/action-items/${id}`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        map((item) => normalizeActionItem(item)),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Updates a checklist or form action item. Success is 200 with a message.
   * Callers should handle 409 ACTION_ITEM_ALREADY_COMPLETED and 404 NOT_FOUND.
   */
  updateActionItem(
    id: number,
    body: ActionItemUpdateRequest
  ): Observable<ActionItemUpdateResponse> {
    return this.http
      .put<ActionItemUpdateResponse>(
        `${this.baseUrl}/action-items/${id}`,
        body,
        { headers: this.headers, withCredentials: true }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Returns service catalogs available to the current portal user.
   * Services for a catalog are loaded separately via getServicesByCatalog.
   */
  getServiceCatalog(): Observable<ServiceCategory[]> {
    return this.http
      .get<unknown>(
        `${this.servicesBaseUrl}/service-catalogs`,
        this.servicesHttpOptions()
      )
      .pipe(
        map((response) =>
          (Array.isArray(response) ? response : []).map((item) =>
            mapServiceCatalog(item)
          )
        ),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getServicesByCatalog(catalogId: string): Observable<Service[]> {
    const options = {
      ...this.servicesHttpOptions(),
      params: new HttpParams().set('catalogIdEq', catalogId),
    };
    return this.http
      .get<unknown>(`${this.servicesBaseUrl}/services`, options)
      .pipe(
        map((response) =>
          (Array.isArray(response) ? response : []).map((item) =>
            mapCatalogService(item, catalogId)
          )
        ),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Returns submitted service requests visible to the current user. This is
   * account-scoped (can include other account members' non-private requests) —
   * the backend is expected to already exclude requests the user isn't
   * allowed to see.
   */
  getServiceRequests(): Observable<ServiceRequestSummary[]> {
    return this.http
      .get<unknown>(
        `${this.servicesBaseUrl}/service-requests`,
        this.servicesHttpOptions()
      )
      .pipe(
        map((response) =>
          (Array.isArray(response) ? response : [])
            .map((item) => mapServiceRequestSummary(item))
            .sort(
              (a, b) =>
                serviceUpdatedAtTime(b.updatedAt) -
                serviceUpdatedAtTime(a.updatedAt)
            )
        ),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Returns a single submitted service request, including the submitted
   * first-step form data. Callers should handle 403 (unauthorized/private
   * request the user can't see) by showing a graceful denial, not partial data.
   */
  getServiceRequestById(id: string): Observable<ServiceRequestDetail> {
    return this.http
      .get<unknown>(
        `${this.servicesBaseUrl}/service-requests/${asServiceId(id)}`,
        this.servicesHttpOptions()
      )
      .pipe(
        map((response) => mapServiceRequestDetail(response)),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          return throwError(() => error);
        })
      );
  }

  /** Returns the current user's own in-progress (unsubmitted) service request drafts. */
  getServiceDrafts(): Observable<ServiceDraft[]> {
    return this.http
      .get<unknown>(
        `${this.servicesBaseUrl}/service-requests/drafts`,
        this.servicesHttpOptions()
      )
      .pipe(
        map((response) =>
          (Array.isArray(response) ? response : [])
            .map((item) => mapServiceDraftListItem(item))
            .sort(
              (a, b) =>
                serviceUpdatedAtTime(b.updatedAt) -
                serviceUpdatedAtTime(a.updatedAt)
            )
        ),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getServiceDraftById(id: string): Observable<ServiceDraft> {
    return this.http
      .get<unknown>(
        `${this.servicesBaseUrl}/service-requests/drafts/${asServiceId(id)}`,
        this.servicesHttpOptions()
      )
      .pipe(
        map((response) => mapServiceDraftDetail(response)),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Autosaves a service request draft. Pass `id` as null on the first save of
   * a new draft (created on the fly); pass the existing draft id on every
   * subsequent autosave.
   */
  saveServiceDraft(
    id: string | null,
    body: ServiceDraftSaveRequest
  ): Observable<ServiceDraft> {
    const payload = toServiceDraftApiBody(body);
    const request$ = id
      ? this.http.put<unknown>(
          `${this.servicesBaseUrl}/service-requests/drafts/${asServiceId(id)}`,
          payload,
          this.servicesHttpOptions()
        )
      : this.http.post<unknown>(
          `${this.servicesBaseUrl}/service-requests/drafts`,
          payload,
          this.servicesHttpOptions()
        );
    return request$.pipe(
      map((response) =>
        mapServiceDraftDetail({
          ...(typeof response === 'object' && response ? response : {}),
          id: id ?? (response as { id?: unknown })?.['id'],
        })
      ),
      catchError((error) => {
        console.error('Error Status Code:', error.status);
        if (error.status == 403 || error.status == '403') {
          this.handleError403();
        }
        return throwError(() => error);
      })
    );
  }

  /** Deletes a service request draft. Only drafts can be deleted, never submitted requests. */
  deleteServiceDraft(id: string): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(
        `${this.servicesBaseUrl}/service-requests/drafts/${asServiceId(
          id
        )}/cancel`,
        {},
        this.servicesHttpOptions()
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Submits the first-step service request form, creating the corresponding
   * task process in Knostos. If `draftId` is provided, posts to the draft
   * submit route; otherwise posts a new service request.
   */
  submitServiceRequest(
    body: ServiceRequestSubmitRequest
  ): Observable<{ id: string; message?: string }> {
    const path = body.draftId
      ? `${this.servicesBaseUrl}/service-requests/drafts/${asServiceId(
          body.draftId
        )}/submit`
      : `${this.servicesBaseUrl}/service-requests`;
    return this.http
      .post<unknown>(
        path,
        toServiceDraftApiBody(body),
        this.servicesHttpOptions()
      )
      .pipe(
        map((response) => mapServiceRequestSubmitResponse(response)),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Function to get all the user blockers based in the user role
   * @param role The user role
   * @returns An observable with an array of blockers
   */
  getBlockerCards(role: string): Observable<Blocker[]> {
    const params = { role };
    return this.http
      .get<Blocker[]>(`${this.baseUrl}/blockers`, {
        headers: this.headers,
        params,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getFacilityCycleInfo(): Observable<FacilityCycle[]> {
    return this.http
      .get<FacilityCycle[]>(`${this.baseUrl}/facility_cycle`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getPatientCycleInfo(): Observable<PatientCycle[]> {
    return this.http
      .get<PatientCycle[]>(`${this.baseUrl}/patient_cycle`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getFacilityCheckInForm(): Observable<CheckInForm> {
    return this.http
      .get<CheckInForm>(`${this.baseUrl}/facility_cycle/check_in/form`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getPatientCheckInForm(): Observable<CheckInForm> {
    return this.http
      .get<CheckInForm>(`${this.baseUrl}/patient_cycle/check_in/form`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to get all the user orders based in the role
   * @param role The user role
   * @returns An observable with the orders based on the user role
   */
  getOrders(role: string): Observable<Order[]> {
    const params = { role };
    return this.http
      .get<Order[]>(`${this.baseUrl}/orders`, {
        headers: this.headers,
        params,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getPatients(showAll = false): Observable<PatientDetails> {
    return this.http
      .get<PatientDetails>(`${this.baseUrl}/patient`, {
        headers: this.headers,
        params: showAll ? new HttpParams().set('show_all', 'true') : undefined,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getPatientById(id: number): Observable<PatientDetails> {
    return this.http
      .get<PatientDetails>(`${this.baseUrl}/patient/${id}`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getPatientsMedications_sultanAPI(id: string) {
    return this.http
      .get(`${this.baseUrl}/patient/${id}/medications`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to get all the prescriptions from a specific user
   * @param id from the patient to get all the prescriptions from
   * @returns Observable with an array of all the prescriptions
   */
  getPatientPrescriptions(id: string): Observable<Prescription[]> {
    return this.http
      .get<Prescription[]>(`${this.baseUrl}/patient/${id}/prescriptions`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  updatePatientProfile(patientID: any, fileName: File): Observable<any> {
    return this.http
      .put(
        `${this.baseUrl}/patient/${patientID}/avatar`,
        { avatar: fileName.name },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  uploadPatientProfile(image: File, url: string): Observable<any> {
    return this.http
      .put(url, image, {
        headers: this.headers,
        // withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to update the image file name and get the url to save the image
   * @param fileName string with the name of the file to update
   * @returns Observable withe the url file and the user `uuid`
   */
  updateUserProfile(
    fileName: string
  ): Observable<{ url: string; uuid: string }> {
    return this.http
      .post<{ url: string; uuid: string }>(
        `${this.baseUrl}/user/attachments`,
        { name: fileName },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to upload the image file
   * @param image the image file to upload
   * @param url the url to update the image file
   * @returns an observable with the image url to store
   */
  uploadUserProfile(image: File, url: string): Observable<any> {
    return this.http
      .put(url, image, {
        headers: this.headers,
        // withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to update the user info
   * @param uuid route to update de user info
   * @param cellPhone the user cell phone
   * @param email the user email
   * @returns an observable with the avatar url
   */
  updateUserInfo(
    uuid: string,
    cellPhone: string,
    email: string
  ): Observable<{ avatar: string }> {
    return this.http
      .put<{ avatar: string }>(
        `${this.baseUrl}/user/me`,
        {
          avatar: uuid,
          cellPhone: cellPhone,
          email: email,
          // smsOptIn: false
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getOrderPrescriptions(id: string): Observable<OrderPrescriptionDoctor> {
    return this.http
      .get<OrderPrescriptionDoctor>(
        `${this.baseUrl}/orders/${id}/prescriptions`,
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Function to get the user deliveries
   * @param id from the user
   * @returns an observable of deliveries from the user
   */
  getDeliveries(id: string): Observable<Delivery[]> {
    return this.http
      .get<Delivery[]>(`${this.baseUrl}/patient/${id}/deliveries`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  resolveBlocker(
    blockerId: any,
    activityId: any,
    type: string,
    value: string
  ): Observable<any> {
    return this.http
      .put(
        `${this.baseUrl}/blockers/resolve`,
        {
          blocker_id: blockerId,
          activity_id: activityId,
          type: type,
          value: value,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  resolveFormBlocker(
    blockerId: any,
    activityId: any,
    type: string,
    form_data: string
  ): Observable<any> {
    return this.http
      .put(
        `${this.baseUrl}/blockers/resolve`,
        {
          blocker_id: blockerId,
          activity_id: activityId,
          type: type,
          form_data: form_data,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  resolveCheckInForm(activityId: any, form_data: {}): Observable<any> {
    return this.http
      .post(
        `${this.baseUrl}/facility_cycle/${activityId}/check_in`,
        {
          form_data: form_data,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  resolvePatientCheckInForm(activityId: any, form_data: {}): Observable<any> {
    return this.http
      .post(
        `${this.baseUrl}/patient_cycle/${activityId}/check_in`,
        {
          form_data: form_data,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Changes the user's account password.
   *
   * @param oldPassword - The user's current password, required for authentication.
   * @param newPassword - The new password to set.
   * @param repeatedPassword - Confirmation of the new password (must match `newPassword`).
   *
   * @returns An `Observable<HttpResponse<string>>` containing the status of the request.
   *          A successful update typically returns HTTP 200 with a plain text confirmation.
   *
   * @example
   * // Change password example
   * changePassword("currentPass123", "newSecurePass!", "newSecurePass!")
   *   .subscribe(response => {
   *     if (response.status === 200) {
   *       console.log("Password updated successfully");
   *     }
   *   });
   */
  changePassword(
    oldPassword: string,
    newPassword: string,
    repeatedPassword: string
  ) {
    return this.http
      .put(
        `${this.baseUrl}/user/password`,
        {
          oldPassword: oldPassword,
          password: newPassword,
          password2: repeatedPassword,
        },
        {
          headers: this.headers,
          withCredentials: true,
          observe: 'response',
          responseType: 'text' as 'json',
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Updates the user's cellphone number after successful OTP verification.
   *
   * @param requestId - The unique identifier of the OTP verification request.
   *                    This ID is returned by the `requestVerification` endpoint.
   * @param cellphone - The new cellphone number to associate with the user's account.
   *
   * @returns An `Observable<ChangeResponse>` containing the status of the update.
   *          A successful update typically returns HTTP 200 with a confirmation message,
   *          such as `"Values changes correctly"`.
   *
   * @example
   * // Change user cellphone after OTP verification
   * changeCellPhone("abc123requestId", "+15555550123").subscribe(...);
   */
  changeCellPhone(
    requestId: string,
    cellphone: string
  ): Observable<ChangeResponse> {
    return this.http
      .put<ChangeResponse>(
        `${this.baseUrl}/user/cellphone`,
        {
          request_id: requestId,
          cellphone: cellphone,
        },
        {
          headers: this.headers,
          withCredentials: true,
          // observe: 'response',
          // responseType: 'text' as 'json',
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Updates the user's email address after successful OTP verification.
   *
   * @param requestId - The unique identifier of the OTP verification request.
   *                    This ID is returned by the `requestVerification` endpoint.
   * @param email - The new email address to associate with the user's account.
   *
   * @returns An `Observable<ChangeResponse>` containing the status of the update.
   *          A successful update typically returns HTTP 200 with a confirmation message,
   *          such as `"Values changes correctly"`.
   *
   * @example
   * // Change user email after OTP verification
   * changeEmail("abc123requestId", "new.email@example.com").subscribe(...);
   */
  changeEmail(requestId: string, email: string): Observable<ChangeResponse> {
    return this.http
      .put<ChangeResponse>(
        `${this.baseUrl}/user/email`,
        {
          request_id: requestId,
          email: email,
        },
        {
          headers: this.headers,
          withCredentials: true,
          // observe: 'response',
          // responseType: 'text' as 'json',
        }
      )
      .pipe(
        tap((response) =>
          console.log('change email successfully ====>', response)
        ),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Requests a new OTP code to be sent to the user via email or SMS.
   *
   * @param channel - The delivery channel for the OTP code:
   *                  - `"EMAIL"` to send the code via email.
   *                  - `"SMS"` to send the code via text message.
   * @param destination - The destination where the OTP should be sent:
   *                      - If `channel` is `"EMAIL"`, this should be the user's email address.
   *                      - If `channel` is `"SMS"`, this should be the user's phone number.
   * @param purpose - The purpose of the OTP request:
   *                  - `"UPDATE_EMAIL"` for verifying an email change.
   *                  - `"UPDATE_CELLPHONE"` for verifying a phone number change.
   *
   * @returns An `Observable<VerificationRequest>` containing details about the request,
   *          such as a `request_id` or other metadata required for validation.
   *
   * @example
   * // Request OTP for email change
   * requestVerification("EMAIL", "user@example.com", "UPDATE_EMAIL").subscribe(...);
   *
   * // Request OTP for phone number change
   * requestVerification("SMS", "+15551234567", "UPDATE_CELLPHONE").subscribe(...);
   */
  requestVerification(
    channel: string,
    destination: string,
    purpose: string
  ): Observable<VerificationRequest> {
    return this.http
      .post<VerificationRequest>(
        `${this.baseUrl}/verification/request`,
        {
          channel: channel,
          destination: destination,
          purpose: purpose,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Verifies an OTP code sent to the user via email or SMS.
   *
   * @param channel - The delivery channel of the OTP code:
   *                  - `"EMAIL"` if the verification is via email.
   *                  - `"SMS"` if the verification is via text message.
   * @param destination - The destination where the OTP was sent:
   *                      - If `channel` is `"EMAIL"`, this will be the user's email address.
   *                      - If `channel` is `"SMS"`, this will be the user's phone number.
   * @param purpose - The purpose of the verification:
   *                  - `"UPDATE_EMAIL"` for updating the user's email address.
   *                  - `"UPDATE_CELLPHONE"` for updating the user's phone number.
   * @param code - The OTP code entered by the user (must be 6 characters long).
   *
   * @returns An `Observable<VerificationCheck>` containing the verification result,
   *          which may include a `request_id` if the verification is successful.
   *
   * @example
   * // Verify code for email change
   * checkVerification("EMAIL", "user@example.com", "UPDATE_EMAIL", "123456").subscribe(...);
   *
   * // Verify code for phone number change
   * checkVerification("SMS", "+15551234567", "UPDATE_CELLPHONE", "654321").subscribe(...);
   */
  checkVerification(
    channel: string,
    destination: string,
    purpose: string,
    code: string
  ): Observable<VerificationCheck> {
    return this.http
      .post<VerificationCheck>(
        `${this.baseUrl}/verification/check`,
        {
          channel: channel,
          destination: destination,
          purpose: purpose,
          code: code,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to get all the doctor documents
   * @returns an observable with all the documents response
   */
  getDocuments(): Observable<DocumentResponse[]> {
    return this.http
      .get<DocumentResponse[]>(`${this.baseUrl}/knostos-documents`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getDocsToReview(): Observable<DocumentResponse[]> {
    return this.http
      .get<DocumentResponse[]>(
        `${
          this.baseUrl
        }/knostos-documents?requires_review=${true}&reviewed=${false}`,
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getRequireReviewDocsCount(): Observable<number> {
    return this.http
      .get<number>(`${this.baseUrl}/knostos-documents/count`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Fuinctions to get the documents of the patient
   * @param id of the patient entity to get documents
   * @returns an observable with an array of patien documents
   */
  getPatientDocuments(id: string): Observable<DocumentInterface[]> {
    return this.http
      .get<DocumentInterface[]>(
        `${this.baseUrl}/knostos-documents?entity_id=${id}`,
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }
  getFormById(id: string, version?: string): Observable<FormType> {
    let params = new HttpParams();
    if (version) {
      params = params.set('version', version);
    }
    return this.http
      .get<FormType>(`${this.baseUrl}/forms/${id}`, {
        headers: this.headers,
        withCredentials: true,
        params,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  reviewDocument(
    id: number | undefined,
    reviewResult: string,
    inputData: string
  ) {
    return this.http
      .put(
        `${this.baseUrl}/knostos-documents/${id}/review`,
        {
          review_result: reviewResult,
          input_data: inputData,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  openDocumentSession(
    id: number,
    type: string,
    reason: string | null
  ): Observable<SessionData> {
    return this.http
      .post<SessionData>(
        `${this.baseUrl}/knostos-documents/${id}/sessions`,
        {
          interaction_type: type,
          download_reason: reason,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Open Session Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  closeDocumentSession(id: number, sessionId: number) {
    return this.http
      .put(
        `${this.baseUrl}/knostos-documents/${id}/sessions/${sessionId}`,
        {},
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Closing Session Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  imagePrescribe(
    patientId: string,
    documentType: string,
    fileB64: string,
    fileName: string,
    orderType: string,
    prescriptionType: string,
    documentNote: string
  ) {
    return this.http
      .post(
        `${this.baseUrl}/patient/${patientId}/prescriptions`,
        {
          document_type: documentType,
          file: fileB64,
          file_name: fileName,
          order_type: orderType,
          prescription_type: prescriptionType,
          document_note: documentNote,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  resolveAttachment(
    blockerId: number,
    activityId: string,
    file: File
  ): Observable<any> {
    return new Observable((observer) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64File = reader.result?.toString().split(',')[1]; // Extract Base64 string
        if (!base64File) {
          observer.error('File conversion failed.');
          return;
        }

        // Perform the HTTP PUT request
        this.http
          .put(
            `${this.baseUrl}/blockers/resolve`,
            {
              blocker_id: blockerId,
              activity_id: activityId,
              type: 'UPLOAD_ATTACHMENT',
              file: base64File,
              file_name: file.name,
            },
            {
              headers: this.headers,
              withCredentials: true,
            }
          )
          .subscribe({
            next: (response) => {
              observer.next(response); // Notify the subscriber of the response
              observer.complete(); // Complete the Observable
            },
            error: (error) => {
              observer.error(error); // Pass the error to the subscriber
              console.error('Error Status Code:', error.status);
              this.handleError403();
            }, // Pass the error to the subscriber
          });
      };
      reader.onerror = (error) => {
        observer.error('Error reading file: ' + error); // Notify the subscriber of the error
      };

      // Read the file as Base64
      reader.readAsDataURL(file);
    });
  }

  getInvoices(role: string): Observable<Invoice[]> {
    return this.http
      .get<Invoice[]>(`${this.baseUrl}/invoices?role=${role}`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  getInvoiceById(id: number): Observable<Invoice> {
    return this.http
      .get<Invoice>(`${this.baseUrl}/invoices/${id}`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Function to get all the submissions
   * @returns An observable with an array of all the submission
   */
  getSubmittedDocuments(): Observable<SentSubmission[]> {
    return this.http
      .get<SentSubmission[]>(`${this.baseUrl}/submissions`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * function to call an specific submission
   * @param id of the submission
   * @returns the entire submission
   */
  getDocumentById(id: number): Observable<SentSubmission> {
    return this.http
      .get<SentSubmission>(`${this.baseUrl}/submissions/${id}`, {
        headers: this.headers,
        withCredentials: true,
      })
      .pipe(
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Calls the backend endpoint responsible for updating the user's username.
   *
   * @param username - The new username to be set.
   * @returns Observable<ChangeResponse>
   *    - On success: returns an object with the structure { message: string }
   *      where `message` contains confirmation details of the update.
   *    - On error: throws an error handled by catchError.
   *
   * Flow:
   * 1. Sends an HTTP PUT request to `${baseUrl}/user/username`,
   *    including the new username in the request body.
   * 2. Custom headers and credentials (cookies/session) are attached.
   * 3. If the request succeeds:
   *    - Logs the server response in the console.
   *    - The Observable emits the `ChangeResponse` object.
   * 4. If the request fails:
   *    - Logs the error status code in the console.
   *    - If the status code is 403 (forbidden), it triggers `handleError403()`.
   *    - The error is re-thrown so it can be handled by the subscriber.
   */
  updateUsername(username: string): Observable<ChangeResponse> {
    return this.http
      .put<ChangeResponse>(
        `${this.baseUrl}/user/username`,
        {
          username,
        },
        {
          headers: this.headers,
          withCredentials: true,
        }
      )
      .pipe(
        tap((response) =>
          console.log('change username successfully ====>', response)
        ),
        catchError((error) => {
          console.error('Error Status Code:', error.status);
          if (error.status == 403 || error.status == '403') {
            this.handleError403();
          }
          return throwError(() => error);
        })
      );
  }

  /**
   * Initializes a new verification session for the current user.
   * ONLY FOR REGISTRATION FLOW
   *
   * @returns Observable with the verification session response.
   */
  verificationSession(): Observable<{ url: string }> {
    return this.http.post<{ url: string }>(
      `${this.baseUrl}/verification/session`,
      {},
      {
        headers: this.headers,
        withCredentials: true,
      }
    );
  }

  /**
   * Verifies the SMS one-time password (OTP) sent to the user during the signup flow.
   *
   * This endpoint validates the OTP associated with a specific signup session.
   * If the verification is successful, the backend confirms the user's phone
   * number and allows the signup process to continue.
   *
   * @param sessionId - Unique identifier of the signup session generated by the backend.
   * @param otp - One-time password received via SMS.
   *
   * @returns An observable containing the verification result and status information.
   */
  verifySms(sessionId: string, otp: string) {
    return this.http.post<ChangeResponse>(
      `${this.baseUrl}/user/portal/signup/verify-sms`,
      {
        session_id: sessionId,
        code: otp,
      },
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Initiates a user session by sending login credentials to the backend.
   *
   * This method performs an HTTP POST request to the authentication endpoint,
   * passing the provided username and password. If the credentials are valid,
   * the backend returns the authenticated user information.
   *
   * Cookies and session data are included in the request via `withCredentials`
   * to support cookie-based authentication.
   *
   * @param username - User identifier used to authenticate in the system.
   * @param password - User password associated with the provided username.
   * @returns An Observable that emits the authenticated user data.
   */
  startSession(
    username: string,
    password: string,
    device_id: string
  ): Observable<LoginUser> {
    return this.http.post<LoginUser>(
      `${this.baseUrl}/user/portal/login`,
      {
        username,
        password,
        device_id,
      },
      { headers: this.headers, withCredentials: true }
    );
  }

  /**
   * Logs in through `/user/login`, the only endpoint that refreshes and returns
   * the Didit validation status. Emits `null` when the backend asks for 2FA.
   */
  refreshValidationThroughLogin(
    credentials: RegistrationCredentials
  ): Observable<LoginUser | null> {
    return this.http
      .post(`${this.baseUrl}/user/login`, credentials, {
        headers: this.headers,
        withCredentials: true,
        responseType: 'text',
      })
      .pipe(
        map((response) =>
          response === '2fa needed' ? null : (JSON.parse(response) as LoginUser)
        )
      );
  }

  /**
   * Validates whether a username is available in the system.
   *
   * Sends a GET request to the backend to check username availability.
   * If the username is already taken or invalid, the backend is expected
   * to return an error response.
   *
   * @param username - Username to be validated
   * @returns Observable<is_available: boolean> - Backend response indicating availability
   */
  validateUsername(username: string): Observable<{ is_available: boolean }> {
    return this.http.get<{ is_available: boolean }>(
      `${this.baseUrl}/user/availability?username=${username}`,
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Creates a new user account in the portal.
   *
   * Sends the required user information to the signup endpoint
   * in order to register a new account.
   *
   * @param username - Desired username for the new account
   * @param password - User password
   * @param password2 - Password confirmation (must match password)
   * @param cell_phone - User mobile phone number
   * @param email - User email address
   *
   * @returns Observable <ChangeResponse> with the backend response
   */
  signup(
    username: string,
    password: string,
    password2: string,
    cell_phone: string,
    email: string
  ): Observable<{ message: string; session_id: string; code: string }> {
    return this.http.post<{
      message: string;
      session_id: string;
      code: string;
    }>(
      `${this.baseUrl}/user/portal/signup`,
      {
        username,
        password,
        password2,
        cell_phone,
        email,
      },
      {
        headers: this.headers,
      }
    );
  }

  /**
   * Sends a request to activate the currently authenticated user's account.
   *
   * This endpoint triggers the activation process for the logged-in user.
   * It requires a valid authentication token in the request headers.
   *
   * @returns {Observable<ChangeResponse>}
   * An observable that emits the server response once the account
   * has been successfully activated.
   *
   * @throws Will emit an error if the activation request fails
   * (e.g., invalid token, expired session, or server error).
   */
  activateAccount(): Observable<ChangeResponse> {
    return this.http.post<ChangeResponse>(
      `${this.baseUrl}/user/me/activate`,
      {},
      {
        headers: this.headers,
        withCredentials: true,
      }
    );
  }
}
